import { useEffect, useState } from 'react'
import { setIn } from 'final-form'
import { useMe, useProfileUpdate } from 'old/api'
import { Me, UpdateUserFormSchema }  from 'old/types'
import { CloudUploadOutlined } from '@ant-design/icons'
import { Loading, PageContentWrapper, PageError, VerticalContainer } from 'old/components/styled'
import { getShopId, isNil, queryClient, useTranslate, useWindowDimensions }  from 'old/utils'
import { Form } from 'react-final-form'
import { message, theme } from 'antd'
import { PageHeader } from 'old/components/page-header/page-header'
import { ProfileInfo } from 'old/components/profile/profile-info/profile-info'
import { useNavigate } from 'react-router-dom'

export const Profile = () => {

    //me:
    const { data: me, isLoading } = useMe()

    //render:
    return isLoading ? <Loading/> : !me ? <PageError/> : <UserFormContent me={me}/>

}

type ContentProps = {
    me: Me
}

export const UserFormContent = ({ me }: ContentProps) => {

    //hooks:
    const { __ } = useTranslate()
    const { token: { padding, margin, marginLG, marginXL } } = theme.useToken()
    const { SM } = useWindowDimensions()
    const navigate = useNavigate()

    //validateFormValues:
    const validateFormValues = (schema: any) => async (values: Me) => {
        if(typeof schema === 'function')
            schema = schema()
        try{
            await schema.validate(values, { abortEarly: false })
        }catch(err: any){
            const errors = err.inner.reduce((formError: object, innerError: any) => {
                return setIn(formError, innerError.path, innerError.message)
            }, {})
            return errors
        }
    }

    //state:
    const [ initialValues, setInitialValues ] = useState(me)
    const [ savingValues, setSavingValues ] = useState(me)

    //mutation:
    const { mutate: updateUser, isLoading, isSuccess, data: mutationResponse } = useProfileUpdate(savingValues)

    //save:
    const save = (values: Me) => {
        setSavingValues(values)
        updateUser()
    }

    //after update:
    useEffect(() => {
        if(isSuccess && !isNil(savingValues) && mutationResponse){
            setInitialValues(savingValues)
            if(initialValues.locale !== savingValues.locale){ // language updated
                const shopId = getShopId()
                queryClient.resetQueries(['config', shopId])
                queryClient.resetQueries(['me'])
            }else{
                queryClient.invalidateQueries(['me'])
            }
            message.success(__`profile_updated`)
        }
    }, [isLoading])

    //render:
    return (
        <PageContentWrapper>

            <Form
                initialValues={initialValues}
                subscription={{ dirty: true }}
                onSubmit={(values: Me) =>
                    UpdateUserFormSchema().validate(values).then(
                        data => save(data as Me)
                    )
                }
                validate={validateFormValues(UpdateUserFormSchema())}
                render={({ form, dirty, handleSubmit }) => (

                    <VerticalContainer style={{ gap: margin }}>

                        {/* header */}
                        <PageHeader
                            title={__`profile`}
                            description={__`manage_your_profile_here`}
                            primaryButton={{
                                name: __`save`,
                                icon: <CloudUploadOutlined/>,
                                mode: isLoading ? 'loading' : dirty ? 'normal': 'disabled',
                                onClick: handleSubmit
                            }}
                            secondaryButton={dirty ? {
                                name: __`discard`,
                                mode: isLoading ? 'disabled' : 'normal',
                                onClick: () => form.reset()
                            } : undefined}
                            backButton={{
                                name: __`back`,
                                onClick: () => navigate('/')
                            }}
                            isFormDirty={dirty}
                        />

                        <VerticalContainer style={{ padding: SM ? `0 ${padding}px` : 0, gap: SM ? marginXL : marginLG }}>

                            {/* info */}
                            <ProfileInfo disableEmail={true} hideRest={false}/>

                        </VerticalContainer>

                    </VerticalContainer>
                    
                )}
            />

        </PageContentWrapper>
    )
    
}
